// src/app/time-attendance/components/payroll-sync/services/payroll-sync.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import {
  ApiPagedResponse,
  ApiResponse,
  BookedHourApprovalDto,
  BookedHourApprovalPatchBody,
  BookedHourHistoryDto,
  BookedHoursHistoryQuery,
  PayableHourDto,
  PayableHoursHistoryQuery,
  PayableHoursQuery,
  PayableHoursRunHistoryDto,
  PayableHoursRunHistoryQuery,
  YywwDto,
  YywwQuery,
} from '../models/payroll-sync.model';

@Injectable({ providedIn: 'root' })
export class PayrollSyncService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  private buildParams(paramsObj: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    Object.entries(paramsObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  getPayableHours(options?: PayableHoursQuery): Observable<ApiPagedResponse<PayableHourDto[]>> {
    const params = this.buildParams({
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
    });

    return this.http
      .get<ApiPagedResponse<PayableHourDto[]>>(`${this.baseUrl}/api/payable-hours`, { params })
      .pipe(map((response) => this.normalizePayableHoursResponse(response)));
  }

  getPayableHoursHistory(
    options?: PayableHoursHistoryQuery,
  ): Observable<ApiPagedResponse<PayableHourDto[]>> {
    return this.getPayableHours(options);
  }

  private normalizePayableHoursResponse(
    response: ApiPagedResponse<PayableHourDto[]>,
  ): ApiPagedResponse<PayableHourDto[]> {
    return {
      status: response?.status ?? 'error',
      totalCount: Number(response?.totalCount ?? 0),
      totalPages: Number(response?.totalPages ?? 0),
      currentPage: Number(response?.currentPage ?? 1),
      pageSize: Number(response?.pageSize ?? 0),
      data: (response?.data ?? []).map((row) => this.normalizePayableHour(row)),
    };
  }

  private normalizePayableHour(row: PayableHourDto): PayableHourDto {
    const payableHoursSyncId = row.payableHoursSyncId ?? row.payableHoursSync?.id ?? null;
    const yywwId = row.yywwId ?? row.yyww?.id;

    return {
      ...row,
      payableHoursSyncId,
      yywwId,
      employee: {
        ...row.employee,
        employeeNumber: row.employee?.employeeNumber ?? null,
        rfid: row.employee?.rfid ?? null,
        reportTo: row.employee?.reportTo ?? null,
        department: row.employee?.department ?? null,
        company: row.employee?.company ?? null,
      },
      project: row.project ?? null,
      company: row.company ?? null,
      department: row.department ?? null,
      workerType: row.workerType ?? null,
      payGroup: row.payGroup ?? null,
      payCode: row.payCode ?? null,
      workOrderNumber: row.workOrderNumber ?? null,
      yyww: row.yyww ?? null,
      createdBy: row.createdBy ?? row.payableHoursSync?.createdBy ?? null,
      createdAt: row.createdAt ?? row.payableHoursSync?.createdAt ?? null,
      payableHoursSync:
        row.payableHoursSync ??
        (payableHoursSyncId
          ? {
              id: payableHoursSyncId,
              yywwId,
              createdAt: row.createdAt ?? undefined,
              createdBy: row.createdBy ?? null,
            }
          : null),
    };
  }

  getRunHistory(
    options?: PayableHoursRunHistoryQuery,
  ): Observable<ApiPagedResponse<PayableHoursRunHistoryDto[]>> {
    const params = this.buildParams({
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
    });

    return this.http.get<ApiPagedResponse<PayableHoursRunHistoryDto[]>>(
      `${this.baseUrl}/api/payable-hours/run-history`,
      { params },
    );
  }

  runPayableHoursByYywwId(yywwId: number): Observable<void> {
    return this.http
      .post<ApiResponse<unknown>>(
        `${this.baseUrl}/api/payable-hours/run-payable-hours-by-yywwId/${yywwId}`,
        {},
      )
      .pipe(map(() => void 0));
  }

  getYywws(options?: YywwQuery): Observable<ApiPagedResponse<YywwDto[]>> {
    const params = this.buildParams({
      searchTerm: options?.searchTerm,
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
    });

    return this.http.get<ApiPagedResponse<YywwDto[]>>(`${this.baseUrl}/api/yywws`, { params });
  }

  getAllYywwsByYear(year: number): Observable<YywwDto[]> {
    const pageSize = 100;

    const initialParams = this.buildParams({
      searchTerm: year,
      pageNumber: 1,
      pageSize,
    });

    return this.http
      .get<ApiPagedResponse<YywwDto[]>>(`${this.baseUrl}/api/yywws`, {
        params: initialParams,
      })
      .pipe(
        expand((res) => {
          if (res.currentPage >= res.totalPages) {
            return of();
          }

          const nextParams = this.buildParams({
            searchTerm: year,
            pageNumber: res.currentPage + 1,
            pageSize,
          });

          return this.http.get<ApiPagedResponse<YywwDto[]>>(`${this.baseUrl}/api/yywws`, {
            params: nextParams,
          });
        }),
        map((res) => res.data ?? []),
        reduce((all, page) => [...all, ...page], [] as YywwDto[]),
        map((weeks) =>
          weeks.sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;

            return a.week - b.week;
          }),
        ),
      );
  }

  getBookedHoursByApprovalStatusByYywwId(
    yywwId: number,
    options: {
      isSupervisorApproved: boolean;
    },
  ): Observable<BookedHourApprovalDto[]> {
    const params = this.buildParams({
      isSupervisorApproved: options.isSupervisorApproved,
    });

    return this.http
      .get<ApiResponse<BookedHourApprovalDto[]>>(
        `${this.baseUrl}/api/booked-hours/get-by-approval-status-by-yywwId/${yywwId}`,
        { params },
      )
      .pipe(map((res) => (res.data ?? []).map((row) => this.normalizeBookedHourApproval(row))));
  }

  getUnapprovedBookedHoursByYywwId(yywwId: number): Observable<BookedHourApprovalDto[]> {
    return this.getBookedHoursByApprovalStatusByYywwId(yywwId, {
      isSupervisorApproved: false,
    }).pipe(
      map((rows) =>
        [...rows].sort((a, b) => {
          const employeeCompare = `${a.employee.lastName} ${a.employee.firstName}`.localeCompare(
            `${b.employee.lastName} ${b.employee.firstName}`,
          );

          if (employeeCompare !== 0) {
            return employeeCompare;
          }

          return a.bookedDate.localeCompare(b.bookedDate);
        }),
      ),
    );
  }

  private normalizeBookedHourApproval(row: BookedHourApprovalDto): BookedHourApprovalDto {
    return {
      ...row,
      employeeApprovedBy: row.employeeApprovedBy ?? row.approvedByEmployee ?? null,
      supervisorApprovedBy: row.supervisorApprovedBy ?? row.approvedBySupervisor ?? null,
      approvedByEmployee: row.approvedByEmployee ?? row.employeeApprovedBy ?? null,
      approvedBySupervisor: row.approvedBySupervisor ?? row.supervisorApprovedBy ?? null,
      project: row.project ?? null,
      company: row.company ?? null,
      department: row.department ?? null,
      workerType: row.workerType ?? null,
      payGroup: row.payGroup ?? row.employee?.payGroup ?? null,
      workOrderNumber: row.workOrderNumber ?? null,
      payCode: row.payCode ?? null,
      departmentShift: row.departmentShift ?? null,
      updatedAt: row.updatedAt ?? null,
      updatedBy: row.updatedBy ?? null,
    };
  }

  patchBookedHourApproval(
    bookedHourId: number,
    body: BookedHourApprovalPatchBody,
  ): Observable<void> {
    return this.http
      .patch<ApiResponse<unknown>>(`${this.baseUrl}/api/booked-hours/${bookedHourId}/approval`, body)
      .pipe(map(() => void 0));
  }

  getBookedHoursHistory(
    options?: BookedHoursHistoryQuery,
  ): Observable<ApiPagedResponse<BookedHourHistoryDto[]>> {
    const params = this.buildParams({
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
    });

    return this.http
      .get<ApiPagedResponse<BookedHourHistoryDto[]>>(`${this.baseUrl}/api/booked-hours`, {
        params,
      })
      .pipe(map((response) => this.normalizeBookedHoursHistoryResponse(response)));
  }

  private normalizeBookedHoursHistoryResponse(
    response: ApiPagedResponse<BookedHourHistoryDto[]>,
  ): ApiPagedResponse<BookedHourHistoryDto[]> {
    return {
      status: response?.status ?? 'error',
      totalCount: Number(response?.totalCount ?? 0),
      totalPages: Number(response?.totalPages ?? 0),
      currentPage: Number(response?.currentPage ?? 1),
      pageSize: Number(response?.pageSize ?? 0),
      data: (response?.data ?? []).map((row) => this.normalizeBookedHourHistory(row)),
    };
  }

  private normalizeBookedHourHistory(row: BookedHourHistoryDto): BookedHourHistoryDto {
    return {
      ...row,
      employee: {
        ...row.employee,
        employeeNumber: row.employee?.employeeNumber ?? '',
        rfid: row.employee?.rfid ?? null,
        reportTo: row.employee?.reportTo ?? null,
        department: row.employee?.department ?? null,
        company: row.employee?.company ?? null,
      },
      employeeApprovedBy: row.employeeApprovedBy ?? row.approvedByEmployee ?? null,
      supervisorApprovedBy: row.supervisorApprovedBy ?? row.approvedBySupervisor ?? null,
      approvedByEmployee: row.approvedByEmployee ?? row.employeeApprovedBy ?? null,
      approvedBySupervisor: row.approvedBySupervisor ?? row.supervisorApprovedBy ?? null,
      project: row.project ?? null,
      company: row.company ?? null,
      department: row.department ?? null,
      workerType: row.workerType ?? null,
      payGroup: row.payGroup ?? null,
      payCode: row.payCode ?? null,
      workOrderNumber: row.workOrderNumber ?? null,
      yyww: row.yyww ?? { id: 0, name: '' },
      departmentShift: row.departmentShift ?? null,
      updatedAt: row.updatedAt ?? null,
      updatedBy: row.updatedBy ?? null,
      createdBy: row.createdBy ?? {
        id: 0,
        firstName: '',
        lastName: '',
        employeeNumber: null,
      },
    };
  }
}